/**
 * SitesOperatorsPage — Phase 24 / Task 24.3 (DEC-094).
 *
 * Dispatcher "Sites & Operators" setup console — the management surface for the
 * hauler catalog junctions (NOT the daily dispatch workflow). Tabs:
 *   - Operators      (hauler_operators + platform-catalog search)
 *   - Tank Batteries (tank_batteries + linked wells + tanks + audit)
 *   - Disposal Wells (hauler_disposal_facilities)
 *   - Disposal Facilities (notes for drivers on the catalog facilities loads go to — #45)
 *   - Requestors     (requestors + per-site/well assignment slots — OPEN-053)
 *
 * Reads are visible to all dispatcher roles (VIEW_HAULER_CATALOG); writes are
 * gated server-side on MANAGE_HAULER_CATALOG (DISPATCH_MANAGER+) and the operator
 * delete on MANAGE_TENANT (TENANT_ADMIN). The nav entry is hidden for non-manager
 * roles in App.tsx as a UX convenience; the server is authoritative.
 */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUserProfile } from '../lib/use-user-timezone';
import { OperatorsTab } from '../features/sites-operators/OperatorsTab';
import { TankBatteriesTab } from '../features/sites-operators/TankBatteriesTab';
import { WellsTab } from '../features/sites-operators/WellsTab';
import { DisposalWellsTab } from '../features/sites-operators/DisposalWellsTab';
import { DisposalFacilitiesTab } from '../features/sites-operators/DisposalFacilitiesTab';
import { RequestorsTab } from '../features/requestors/RequestorsTab';
import { MANAGE_ROLES } from '../lib/role-gates';
import { useUrlParam } from '../lib/url-state';

type Tab =
  | 'operators'
  | 'tank-batteries'
  | 'wells'
  | 'disposal-wells'
  | 'disposal-facilities'
  | 'requestors';

/**
 * The page is deep-linkable: `?tab=<tab>&operatorId=<id>` lands on a tab and, for
 * the operators tab, opens that customer straight into its pricing form. The
 * InReview no-rate banner uses this so a reviewer can fix the rate in one click
 * instead of being told where to go looking. An unknown tab value falls back to
 * the default rather than rendering nothing.
 */
function isTab(value: string | null): value is Tab {
  return (
    value === 'operators' ||
    value === 'tank-batteries' ||
    value === 'wells' ||
    value === 'disposal-wells' ||
    value === 'disposal-facilities' ||
    value === 'requestors'
  );
}

// The tabs' in-page detail params (WellsTab, TankBatteriesTab, OperatorsTab, RequestorsTab,
// DisposalFacilitiesTab) plus the operators deep link.
const TAB_RESETS = ['well', 'battery', 'operator', 'requestor', 'facility', 'operatorId'];

const TENANT_ADMIN_ROLES: ReadonlySet<string> = new Set(['TENANT_ADMIN', 'PLATFORM_ADMIN']);

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 14px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-accent-blue)' : '2px solid transparent',
        backgroundColor: 'transparent',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
      }}
    >
      {label}
    </button>
  );
}

export function SitesOperatorsPage(): React.JSX.Element {
  const { data: profileData } = useUserProfile();
  const role = profileData?.profile.role ?? '';
  const canManage = MANAGE_ROLES.has(role);
  const isTenantAdmin = TENANT_ADMIN_ROLES.has(role);
  const [searchParams] = useSearchParams();
  const focusOperatorId = searchParams.get('operatorId');
  // OPEN-191 — the tab lives in `?tab=` (it was read once and never written), so Back, a refresh
  // and a shared link land on it. Switching tab closes whatever detail the tabs had open, and
  // drops the one-shot `operatorId` link so coming back does not reopen its form; each tab's
  // own filters stay.
  const [requestedTab, setTab] = useUrlParam('tab', 'operators', TAB_RESETS);
  const tab: Tab = isTab(requestedTab) ? requestedTab : 'operators';

  return (
    <div style={{ padding: '20px', maxWidth: '900px' }}>
      <div
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--color-border-secondary)',
          marginBottom: '18px',
        }}
      >
        <TabButton
          active={tab === 'operators'}
          label="Operators"
          onClick={(): void => setTab('operators')}
        />
        <TabButton
          active={tab === 'tank-batteries'}
          label="Sites"
          onClick={(): void => setTab('tank-batteries')}
        />
        <TabButton active={tab === 'wells'} label="Wells" onClick={(): void => setTab('wells')} />
        <TabButton
          active={tab === 'disposal-wells'}
          label="Disposal Wells"
          onClick={(): void => setTab('disposal-wells')}
        />
        <TabButton
          active={tab === 'disposal-facilities'}
          label="Disposal Facilities"
          onClick={(): void => setTab('disposal-facilities')}
        />
        <TabButton
          active={tab === 'requestors'}
          label="Requestors"
          onClick={(): void => setTab('requestors')}
        />
      </div>

      {tab === 'operators' && (
        <OperatorsTab
          canManage={canManage}
          isTenantAdmin={isTenantAdmin}
          focusOperatorId={focusOperatorId}
        />
      )}
      {tab === 'tank-batteries' && <TankBatteriesTab canManage={canManage} />}
      {tab === 'wells' && <WellsTab canManage={canManage} />}
      {tab === 'disposal-wells' && <DisposalWellsTab canManage={canManage} />}
      {tab === 'disposal-facilities' && <DisposalFacilitiesTab canManage={canManage} />}
      {tab === 'requestors' && <RequestorsTab canManage={canManage} />}
    </div>
  );
}
